import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OrganizationsFilter } from '../../../../core/filters/organizations.filter';
import { BillingStatuses, OrganizationStatuses, PricingTiers } from '../../../../core/enums/data.enums';
import { MultiSelectComponent } from "../../../../parts/multi-select/multi-select.component";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ParentFocusDirective } from "../../../../core/directives/parent-focus.directive";
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { LocalStorageKeyConstants } from '../../../../core/constants/constants';
import { BaseSearchFilterComponent } from '../../../../core/base-classes/base-search-filter.component';

@Component({
  selector: 'ms-organization-search-filter',
  imports: [CommonModule, FormsModule, MultiSelectComponent, FontAwesomeModule, ParentFocusDirective],
  templateUrl: './organization-search-filter.component.html'
})
export class OrganizationSearchFilterComponent extends BaseSearchFilterComponent<OrganizationsFilter> implements OnInit {

  public OrganizationStatuses = OrganizationStatuses;
  public BillingStatuses = BillingStatuses;
  public PricingTiers = PricingTiers;
  public selectStatusOptions = [OrganizationStatuses.Active, OrganizationStatuses.Inactive, OrganizationStatuses.Deleted];
  public selectBillingStatusOptions = [BillingStatuses.Paid, BillingStatuses.Demo, BillingStatuses.Trial, BillingStatuses.Suspended];
  public selectPricingTierOptions = [PricingTiers.Custom, PricingTiers.Premium, PricingTiers.Standard, PricingTiers.Trial, PricingTiers.WhiteGlove];

  // Input field values - these are bound to the actual input fields
  public organizationNameInput = '';

  public selectedOrganizationStatusOptions = signal<OrganizationStatuses[]>([OrganizationStatuses.Active]);
  public selectedBillingStatusOptions = signal<BillingStatuses[]>([]);
  public selectedPricingTierOptions = signal<PricingTiers[]>([]);

  constructor(public userSessionService: UserSessionService) {
    super();
    this.selectedOrganizationStatusOptions.set(this.activeFilter.statuses || [OrganizationStatuses.Active]);
    this.selectedBillingStatusOptions.set(this.activeFilter.billingStatuses || []);
    this.selectedPricingTierOptions.set(this.activeFilter.pricingTiers || []);
  }

  public override get activeFilter(): OrganizationsFilter {
    return this.userSessionService.organizationsFilter;
  }

  protected override getDefaultFilter(): OrganizationsFilter {
    return ListConstants.ORGANIZATIONS_FILTER_DEFAULT();
  }

  // Platform-level list — every field in the dropdown is user-selectable, so nothing is preserved.
  protected override get preservedFilterKeys(): (keyof OrganizationsFilter)[] {
    return [];
  }

  protected override get dropdownSelector(): string {
    return '#organization-search-filter-dropdown';
  }

  protected override syncStateFromFilter(): void {
    this.selectedOrganizationStatusOptions.set([...(this.activeFilter.statuses || [])]);
    this.selectedBillingStatusOptions.set([...(this.activeFilter.billingStatuses || [])]);
    this.selectedPricingTierOptions.set([...(this.activeFilter.pricingTiers || [])]);
  }

  protected override saveToStorage(): void {
    this.userSessionService.saveToLocalStorage(LocalStorageKeyConstants.ORGANIZATIONS_FILTER(), this.activeFilter);
  }

  public ngOnInit(): void {
    this.onApply();
  }

  public onStatusChange(event: any) {
    this.activeFilter.statuses = event;
    this.saveToStorage();
  }

  public onPricingTierChange(event: any) {
    this.activeFilter.pricingTiers = event;
    this.saveToStorage();
  }

  public onBillingStatusChange(event: any) {
    this.activeFilter.billingStatuses = event;
    this.saveToStorage();
  }

  public override setFilterCount(): void {
    this.filterCount = (this.activeFilter.names?.length || 0) +
      (this.activeFilter.billingStatuses?.length || 0) +
      (this.activeFilter.pricingTiers?.length || 0) +
      (this.activeFilter.statuses?.length || 0);
  }

  public addName(): void {
    if (this.organizationNameInput && !(this.organizationNameInput.trim() === '')) {
      this.activeFilter.names?.push(this.organizationNameInput);
      this.saveToStorage();
    }
    this.organizationNameInput = '';
  }

  public removeName(index: number): void {
    this.activeFilter.names?.splice(index, 1);
    this.saveToStorage();
  }

  public removeStatus(index: number): void {
    this.activeFilter.statuses?.splice(index, 1);
    this.saveToStorage();
  }
}
